/* eslint no-bitwise: 0 */
import { RootState } from 'gf-types';
import { appContext } from '../AppContext';
import { GoFormzServices } from '../AppSettings';
import { apimCall } from './api';
import { AppFeatures, FeatureDefaults, Features, Operations, Settings } from './PermissionUtility-types';
import AppFeatureCanIUse from './PermissionUtilityAppFeatures';
import { analyticsGroup } from './AnalyticsUtility';
import moment from 'moment';

const invokedExperiments = {};
function tryInvokeExperiment(state: RootState, feature: string, value: string | boolean | number) {
  const experiments = state.profile.featureExperiments || {};

  //no experiment for this feature
  if (!experiments.hasOwnProperty(feature)) {
    return;
  }

  const triggeringExperiment = experiments[feature];
  const now = new Date();
  const experimentRequalifyTime = moment(now).add(triggeringExperiment.requalifyMinutes ?? 24 * 60, 'minutes');
  const localStorageKey = `${state.profile.clientId}:ffexp:${triggeringExperiment.featureFlagId}:${triggeringExperiment.featureFlagExperimentId}`;

  //check if we have notified already
  const currentLocalStorageValue = localStorage.getItem(localStorageKey);
  if (currentLocalStorageValue != null && Date.parse(currentLocalStorageValue) > now.getTime()) {
    return;
  }

  //secondary check so that we dont get multiple inflight requests happening since the api call in a promise
  if (invokedExperiments.hasOwnProperty(triggeringExperiment.featureFlagExperimentId)) {
    return;
  }

  invokedExperiments[triggeringExperiment.featureFlagExperimentId] = true;
  //notify experiment triggered
  apimCall<{ segmentId: string; segmentHistoricalId: string }>(
    GoFormzServices.AccountService,
    `/clientexperiments/${triggeringExperiment.featureFlagExperimentId}?isExperimentGroup=${triggeringExperiment.isExperimentGroup}`,
    'POST'
  )
    .then((result) => {
      delete invokedExperiments[triggeringExperiment.featureFlagExperimentId];
      if (result.status == 200) {
        //store in browser cache, this way we dont excessively notify
        localStorage.setItem(localStorageKey, experimentRequalifyTime.toString());

        const clientId = state?.profile?.clientId;
        if (clientId == null) {
          return;
        }

        const analyticsTraits = {};
        const segmentHistoricalId = result.data?.segmentHistoricalId;
        if (segmentHistoricalId != null) {
          const historicalExperimentValue = (window?.analytics?.group()?.traits()?.clientFeatureExperimentation ?? '') + segmentHistoricalId + ';';
          analyticsTraits['clientFeatureExperimentation'] = historicalExperimentValue;
        }

        const segmentId = result.data?.segmentId;
        if (segmentId != null) {
          analyticsTraits[segmentId] = value == null ? 'null' : value?.toString();
        }

        analyticsGroup(clientId, analyticsTraits);
      }
    })
    .catch((ex) => {
      delete invokedExperiments[triggeringExperiment.featureFlagExperimentId];
      console.error('Failed to qualify for experiment', ex);
    });
}

function getFeatureValue(state: RootState, feature: Features, triggerExperiment = true) {
  const getValue = (featureLookup) => {
    const featureKey = Object.getOwnPropertyNames(featureLookup ?? {}).find((key) => key?.toLowerCase() === feature?.toLowerCase());
    return featureKey ? featureLookup[featureKey] : undefined;
  };

  //early return because we do not trigger experiments on public forms
  if (state.formeditor?.sharedAccessToken) {
    return getValue(state.formeditor.features);
  }

  const value = getValue(state.profile.features);
  if (value !== undefined) {
    //check if we need to invoke experiment
    triggerExperiment && tryInvokeExperiment(state, feature, value);
    return value;
  }

  //fallback to the default value or undefined
  return getValue(FeatureDefaults);
}

export { AppFeatures, Features, Operations, Settings };

export function isPublicForm() {
  const state = appContext.store.getState();
  return !!state.formeditor.sharedAccessToken;
}

export function hasBinaryPermission(operation: Operations) {
  const state = appContext.store.getState();
  let result = false;
  const op = state.userInfo.bp;
  if ((op & (1 << (operation - 1))) !== 0) {
    result = true;
  }
  return result;
}

export function getSetting(setting: Settings) {
  const state = appContext.store.getState();
  return state.profile.settings[setting];
}

export function clientHasPermissionToTier(subscription: string): boolean {
  const permissionLevels = {
    deprecated: 0,
    // teacher tier does not have access to workflwos
    teacher: 0,
    basic: 1,
    team: 2,
    advanced: 3,
    trial: 4,
    enterprise: 5,
    // below permerissions can only be added from client override
    unlisted: 6,
    addon: 6
  };
  const state = appContext.store.getState();
  const clientSubscription: string = state.profile.client.subscription;

  const perm1: string = clientSubscription.replace('gf-', '');

  return permissionLevels[perm1] >= permissionLevels[subscription];
}

export function getFeature(feature: Features, triggerExperiment = true) {
  const state = appContext.store.getState();
  return getFeatureValue(state, feature, triggerExperiment);
}

export function canIUse(feature: AppFeatures | Features, triggerExperiment = true): boolean {
  const state = appContext.store.getState();

  //check if feature is an app feature
  if (AppFeatureCanIUse.hasOwnProperty(feature)) {
    return AppFeatureCanIUse[feature](state);
  }

  //else fallback to the feature flags
  return getFeature(feature as Features, triggerExperiment) as boolean;
}
